export const WEBFLOW_EMBED_CSS_WINDOW_KEY = "__VIABLE_WEBFLOW_EMBED_CSS__";

declare global {
	interface Window {
		__VIABLE_WEBFLOW_EMBED_CSS__?: string;
	}
}

/**
 * Contain the Webflow host so embed z-index (S4 z-50) cannot paint over sibling
 * navbar/footer, without `overflow` (that would break sticky S2/S3 stages).
 */
export const EMBED_HOST_CSS =
	":host{display:block;width:100%;position:relative;isolation:isolate;-webkit-text-stroke:none;}";

export function readCapturedEmbedCss(): string {
	if (typeof window === "undefined") {
		return "";
	}

	const value = window[WEBFLOW_EMBED_CSS_WINDOW_KEY];
	return typeof value === "string" ? value : "";
}

export function composeEmbedStyleText(baseCss: string, capturedCss = ""): string {
	return `${EMBED_HOST_CSS}\n${baseCss}\n${capturedCss}`;
}

const TW_PROPERTY_HOIST_KEY = "viable-webflow-tw-properties";

export function extractPropertyRegistrations(cssText: string): string[] {
	return cssText.match(/@property\s+[^{]+\{[^}]*\}/g) ?? [];
}

/**
 * Browsers ignore @property registrations inside shadow roots, so Tailwind v4
 * variable defaults (e.g. `--tw-translate-x`) stay unset there and every utility
 * composing them (`translate: var(--tw-translate-x) …`) computes to `none`.
 * Registrations are document-global, so hoisting them to `document.head` restores
 * the defaults inside the shadow tree. Only registrations are hoisted — style
 * rules still live exclusively in the shadow root.
 */
export function hoistTailwindPropertyRegistrations(cssText: string): void {
	if (typeof document === "undefined") {
		return;
	}
	if (document.head.querySelector(`style[data-${TW_PROPERTY_HOIST_KEY}]`)) {
		return;
	}
	const rules = extractPropertyRegistrations(cssText);
	if (rules.length === 0) {
		return;
	}
	const styleEl = document.createElement("style");
	styleEl.setAttribute(`data-${TW_PROPERTY_HOIST_KEY}`, "true");
	styleEl.textContent = rules.join("\n");
	document.head.appendChild(styleEl);
}

export function applyEmbedHostIsolation(host: HTMLElement): void {
	host.style.display = "block";
	host.style.width = "100%";
	host.style.position = "relative";
	host.style.isolation = "isolate";
}

export function measureEmbedContentHeight(root: HTMLElement): number {
	const rootBox = root.getBoundingClientRect();
	let bottom = rootBox.bottom;
	for (const node of root.querySelectorAll("*")) {
		const box = node.getBoundingClientRect();
		if (box.bottom > bottom) {
			bottom = box.bottom;
		}
	}
	return Math.max(0, Math.ceil(Math.max(rootBox.height, bottom - rootBox.top)));
}

export function syncEmbedHostHeight(host: HTMLElement, content: HTMLElement): () => void {
	const apply = () => {
		const height = measureEmbedContentHeight(content);
		if (height > 0) {
			host.style.minHeight = `${height}px`;
		}
	};

	apply();
	if (typeof ResizeObserver === "undefined") {
		return () => undefined;
	}

	const observer = new ResizeObserver(apply);
	observer.observe(content);
	return () => observer.disconnect();
}
